import { motion } from "framer-motion";
import {
  CalendarClock,
  CheckCircle2,
  Luggage,
  MapPin,
  MessageCircle,
  PlaneLanding,
  Users,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const WHATSAPP_LINK =
  "https://wa.me/966540423401?text=Hello%20BlackRide%0A%0AI%20would%20like%20to%20arrange%20a%20Jeddah%20Airport%20transfer.%0A%0AFlight%20number%3A%0AArrival%20time%3A%0ADestination%3A%0APassengers%3A%0ALuggage%3A%0APreferred%20vehicle%20class%3A";

const COPY = {
  en: {
    eyebrow: "Booking Flow",
    title: "A simpler way to arrange a premium airport transfer",
    subtitle:
      "You do not need to fill a long public checkout. Share the key trip details once on WhatsApp and BlackRide can recommend the right vehicle, confirm availability, and coordinate the arrival properly.",
    checklistTitle: "What to send first",
    checklist: [
      {
        icon: PlaneLanding,
        title: "Flight or arrival details",
        desc: "Arrival airport, flight timing, and whether meet-and-greet is needed at the terminal.",
      },
      {
        icon: MapPin,
        title: "Destination and stop notes",
        desc: "Hotel in Makkah, residence, Jeddah city address, or any additional stops to plan around.",
      },
      {
        icon: Users,
        title: "Passengers and family setup",
        desc: "Adults, children, elders, and anything that affects boarding comfort or seating.",
      },
      {
        icon: Luggage,
        title: "Luggage and vehicle preference",
        desc: "Bag count, stroller needs, and whether you prefer sedan, SUV, or executive van comfort.",
      },
    ],
    sideEyebrow: "Why this works better",
    sideTitle: "More like concierge coordination, less like a generic ride form.",
    steps: [
      "Share your route once and avoid repeating the same details later.",
      "Get guidance on the right class for airport, family, executive, or Umrah travel.",
      "Stay connected with a real operations team before the pickup actually happens.",
    ],
    cta: "Start on WhatsApp",
  },
  ar: {
    eyebrow: "آلية الحجز",
    title: "طريقة أبسط لترتيب نقل فاخر من المطار",
    subtitle:
      "لست بحاجة إلى نموذج عام طويل. أرسل تفاصيل الرحلة الأساسية مرة واحدة عبر واتساب، وسيوجهك فريق بلاك رايد إلى المركبة المناسبة ويؤكد التوفر وينسق الوصول بشكل صحيح.",
    checklistTitle: "ما الذي ترسله أولاً",
    checklist: [
      {
        icon: PlaneLanding,
        title: "تفاصيل الرحلة أو الوصول",
        desc: "مطار الوصول ووقت الرحلة وما إذا كنت تحتاج إلى خدمة الاستقبال عند البوابة.",
      },
      {
        icon: MapPin,
        title: "الوجهة وملاحظات التوقف",
        desc: "فندق في مكة أو عنوان داخل جدة أو أي توقفات إضافية يجب التخطيط لها.",
      },
      {
        icon: Users,
        title: "عدد الركاب وطبيعة المجموعة",
        desc: "البالغون والأطفال وكبار السن وكل ما يؤثر على راحة الصعود والجلوس.",
      },
      {
        icon: Luggage,
        title: "الأمتعة وفئة المركبة",
        desc: "عدد الحقائب واحتياجات العربات وما إذا كنت تفضل سيدان أو SUV أو فان تنفيذي.",
      },
    ],
    sideEyebrow: "لماذا هذه الطريقة أفضل",
    sideTitle: "أقرب إلى تنسيق كونسيرج راقٍ وأبعد عن نموذج حجز عام.",
    steps: [
      "شارك مسارك مرة واحدة وتجنب إعادة نفس التفاصيل لاحقاً.",
      "احصل على ترشيح واضح لفئة المركبة المناسبة للمطار أو العائلة أو الأعمال أو العمرة.",
      "ابقَ على تواصل مع فريق عمليات حقيقي قبل موعد الوصول الفعلي.",
    ],
    cta: "ابدأ عبر واتساب",
  },
};

export default function QuickBookingSection() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;

  return (
    <section className="relative bg-[#060606] py-20 md:py-24">
      <div className="mx-auto grid max-w-7xl gap-6 px-6 md:px-12 lg:grid-cols-[1.08fr_0.92fr]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="rounded-[32px] border border-white/10 bg-[#121212] p-8 md:p-10"
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-[0.26em] text-[#C7A239]">{copy.eyebrow}</p>
          <h2 className="max-w-3xl font-['Playfair_Display'] text-3xl font-semibold leading-tight text-white md:text-4xl">
            {copy.title}
          </h2>
          <p className="mt-4 max-w-3xl text-base leading-8 text-[#b9b0a3]">{copy.subtitle}</p>

          <p className="mt-10 mb-5 text-sm uppercase tracking-[0.22em] text-[#8f8677]">{copy.checklistTitle}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {copy.checklist.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.title} className="rounded-[24px] border border-white/8 bg-[#0d0d0d] p-5">
                  <Icon className="mb-4 h-5 w-5 text-[#C7A239]" />
                  <h3 className="text-lg font-medium text-white">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-[#9f978b]">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="flex flex-col justify-between rounded-[32px] border border-[#C7A239]/20 bg-[radial-gradient(circle_at_top,rgba(199,162,57,0.14),transparent_52%),#0b0b0b] p-8 md:p-10"
        >
          <div>
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#C7A239]/10">
              <MessageCircle className="h-7 w-7 text-[#C7A239]" />
            </div>
            <p className="mb-3 text-xs uppercase tracking-[0.24em] text-[#C7A239]">{copy.sideEyebrow}</p>
            <h3 className="font-['Playfair_Display'] text-3xl leading-tight text-white">{copy.sideTitle}</h3>
            <div className="mt-8 space-y-4">
              {copy.steps.map((step) => (
                <div key={step} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-[#C7A239]" />
                  <p className="text-sm leading-7 text-[#c8c1b5]">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-[#C7A239] px-6 py-4 font-semibold text-black transition hover:bg-[#D4B34A]"
            data-testid="quick-booking-whatsapp-cta"
            data-track="quick_booking_whatsapp_cta"
          >
            <MessageCircle className="h-5 w-5" />
            {copy.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
